import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { FunctionQueueStatus, FunctionRun } from '../../core/api/models/function-run.model';
import { PagedResult } from '../../core/api/models/pagination.model';
import { FunctionRunsApiService } from '../../core/api/services/function-runs-api.service';
import { localDateInputToUtcRange } from '../../core/date/local-day-range';
import { formatRunDuration } from '../../shared/ui/duration/duration';
import {
  PaginationChange,
  PaginationComponent,
} from '../../shared/ui/pagination/pagination.component';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../shared/ui/pagination/pagination.config';
import {
  emptyRunFilters,
  RunFilterValue,
  RunFiltersComponent,
} from '../../shared/ui/run-filters/run-filters.component';

@Component({
  selector: 'app-function-runs',
  standalone: true,
  imports: [CommonModule, RouterLink, PaginationComponent, RunFiltersComponent],
  templateUrl: './function-runs.component.html',
  styleUrl: './function-runs.component.scss',
})
export class FunctionRunsComponent implements OnInit {
  private readonly api = inject(FunctionRunsApiService);

  data: PagedResult<FunctionRun> | null = null;
  paging: PaginationChange = { page: DEFAULT_PAGE, pageSize: DEFAULT_PAGE_SIZE };
  filters: RunFilterValue = emptyRunFilters();
  loading = false;
  error = '';

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const dateRange = localDateInputToUtcRange(this.filters.fromDate, this.filters.toDate);
    this.loading = true;
    this.error = '';
    this.api.getRuns(this.paging.page, this.paging.pageSize, {
      search: this.filters.search || undefined,
      source: this.filters.source || undefined,
      functionType: this.filters.functionType || undefined,
      triggerSource: this.filters.triggerSource || undefined,
      status: this.filters.status || undefined,
      fromUtc: dateRange.fromUtc,
      toUtc: dateRange.toUtc,
    })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: value => {
          this.data = value;
          this.paging = { page: value.page, pageSize: value.pageSize };
        },
        error: () => this.error = 'Could not load run history.',
      });
  }

  changePagination(paging: PaginationChange): void {
    this.paging = paging;
    this.load();
  }

  applyFilters(filters: RunFilterValue): void {
    this.filters = filters;
    this.paging = { ...this.paging, page: DEFAULT_PAGE };
    this.load();
  }

  rowIndex(index: number): number {
    return (this.paging.page - 1) * this.paging.pageSize + index + 1;
  }

  duration(run: FunctionRun): string {
    return formatRunDuration(run.startedAtUtc, run.completedAtUtc);
  }

  statusClass(status: FunctionQueueStatus): string {
    return `status ${status.toLowerCase()}`;
  }
}
